import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Tooltip,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import useBoolean from "hooks/useBoolean";
import { pick } from "lodash";
import React, { useMemo, useState } from "react";
import { FormProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NoteIcon from "@mui/icons-material/Note";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  CertificateThemeRequest,
  ICertificateTheme,
} from "services/KnowfullyAdminClient";
import { CommonFormProps, Orientation } from "types/common-types";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

const styles = {
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
};

const { Form } = withTypes<CertificateThemeRequest>();

const CertificateThemeForm = ({
  entity: certificateTheme,
  submitLabel,
  onCancel,
  onSubmit,
}: CommonFormProps<ICertificateTheme, CertificateThemeRequest> &
  FormProps<CertificateThemeRequest>) => {
  const { formatMessage: t } = useIntl();
  const bodyTag = "[[BODY]]";
  const [bodyTagError, setBodyTagError] = useBoolean(false);
  const [orientation, setOrientation] = useState(
    certificateTheme?.isLandscape ? Orientation.Landscape : Orientation.Portrait
  );
  const init = useMemo(
    () => ({
      orientation: certificateTheme?.isLandscape
        ? Orientation.Landscape
        : Orientation.Portrait,
      ...pick(certificateTheme ?? {}, [
        "name",
        "description",
        "value",
        "isLandscape",
        "themeHtml",
        "organizationId",
      ]),
    }),
    [certificateTheme]
  );

  return useMemo(() => {
    const onInnerSubmit = (values: CertificateThemeRequest) => {
      values.isLandscape = orientation === Orientation.Landscape;
      if (values.themeHtml?.includes(bodyTag)) {
        setBodyTagError.setFalse();
        return onSubmit(values);
      }

      setBodyTagError.setTrue();
    };

    return (
      <Form
        onSubmit={onInnerSubmit}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values, form }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={form.change}
                  mode={
                    certificateTheme
                      ? ContextOrganizationFieldMode.EditManuallyTriggered
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />
                <Field name="name" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "certificateThemeFormNameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="nameText"
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="value" required>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "certificateThemeFormValueLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="valueText"
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        multiline={true}
                        rows={4}
                        fullWidth
                        label={t({
                          id: "certificateThemeFormDescriptionLabel",
                        })}
                        InputLabelProps={{ required: false }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="descriptionText"
                      />
                    </Grid>
                  )}
                </Field>
                <Field name="orientation">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <h3 style={{ fontWeight: "normal", color: "grey" }}>
                        {t({
                          id: "orientation",
                        })}
                      </h3>
                      <ToggleButtonGroup
                        exclusive
                        onChange={(e, value) => setOrientation(value)}
                        size="large"
                      >
                        <Tooltip
                          title={t({
                            id: "landscape",
                          })}
                        >
                          <ToggleButton
                            value={Orientation.Landscape}
                            selected={orientation === Orientation.Landscape}
                            data-cy="landscapeButton"
                          >
                            <NoteIcon />
                          </ToggleButton>
                        </Tooltip>
                        <Tooltip
                          title={t({
                            id: "portrait",
                          })}
                        >
                          <ToggleButton
                            value={Orientation.Portrait}
                            selected={orientation === Orientation.Portrait}
                            data-cy="portraitButton"
                          >
                            <InsertDriveFileIcon />
                          </ToggleButton>
                        </Tooltip>
                      </ToggleButtonGroup>
                    </Grid>
                  )}
                </Field>

                <Field name="themeHtml">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FieldRichText
                        input={input}
                        meta={meta}
                        label={t({ id: "certificateThemeFormContentLabel" })}
                        init={{
                          height: 470,
                          paste_data_images: true,
                        }}
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}

                {bodyTagError && (
                  <Alert sx={styles.feedback} severity="error">
                    <T id="bodyTagErrorMessage" />
                  </Alert>
                )}
              </Grid>
            </DialogContent>

            <DialogActions>
              {submitting && <CircularProgress size={24} />}
              <Button
                disabled={submitting}
                onClick={onCancel}
                data-cy="cancelButton"
              >
                {t({ id: "cancelButton" })}
              </Button>

              <Button
                type="submit"
                color="primary"
                disabled={submitting}
                data-cy="saveButton"
              >
                {submitLabel}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    );
  }, [
    init,
    orientation,
    setBodyTagError,
    onSubmit,
    certificateTheme,
    bodyTagError,
    onCancel,
    t,
    submitLabel,
  ]);
};

export default CertificateThemeForm;
